import { css } from "@linaria/core";
import React from "react";
import type { SingleValue } from "react-select";
import { useSources } from "../../../api/useSources";
import t from "../../../i18n";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import ReactSelect from "@ui/ReactSelect";
import type { BaseOption } from "@ui/ReactSelect/useFormatOptionLabel";
import Text from "@ui/Text";
import { spacings } from "@ui/Theme";

export default function SourceIdSelector({ onChange }: { onChange: (value: string) => void }) {
  const sources = useSources();
  const [error, setError] = React.useState<boolean>(false);

  const sortedSources = React.useMemo(
    () => sources.sort((a, b) => a.id.localeCompare(b.id)),
    [sources],
  );

  const [sourceIdOptions, setSourceIdOptions] = React.useState<BaseOption[]>(
    sortedSources.map(source => ({ label: source.id, value: source.id })),
  );

  const handleChange = React.useCallback(
    (newValue: SingleValue<BaseOption>) => {
      if (newValue === null || newValue.value === null) return;
      setError(false);
      onChange(newValue.value);
    },
    [onChange],
  );

  const handleOptionCreation = React.useCallback(
    (value: string) => {
      if (/^[a-zA-Z0-9_]{0,49}$/.test(value) === false) return setError(true);
      setError(false);
      setSourceIdOptions(prev => [...prev, { label: value, value }]);
      onChange(value);
    },
    [onChange],
  );

  return (
    <div>
      <div className={ContainerSourceIdSelector}>
        <Text type="body1SemiBold">
          {t("dataSource.import.source_id_input_name")}
          <sup style={{ color: "var(--darkNitrogen600" }}>*</sup>
        </Text>
        <ReactSelect
          menuPortalTarget={window.document.body}
          placeholder="Source"
          onChange={handleChange}
          options={sourceIdOptions}
          onCreate={handleOptionCreation}
        />
      </div>
      {error && (
        <AdditionnalMessage
          className={SourceIdSelectorErrorgMessage}
          status="ERROR"
          text={t("dataSource.import.error.unexpected_source_id")}
        />
      )}
    </div>
  );
}

const ContainerSourceIdSelector = css`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const SourceIdSelectorErrorgMessage = css`
  margin-top: ${spacings.xs};
`;
